import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import BreakingNews from "../../components/BreakingNews";
import NewsCard from "../../components/NewsCard";
import RightSidebar from "../../components/RightSidebar";
import useNews from "../../hooks/useNews";
import ViewAllHeader from "../../common/ViewAllHeader";
import { getNewsByFilter } from "../../api/apiHandler";
import { getMediaBaseUrl } from "../../utils/backendUrl";
import { videonews } from "../home/homeData.js";
import TopAdBanner from "../../components/TopAdBanner";
import LeftAdBanner from "../../components/LeftAdBanner";
import WeatherWidget from "../../components/WeatherWidget";
import logo from "../../Assets/images/mlogo.png";
const PER_PAGE = 8;

const NewsPage = ({
  language,
  state,
  category,
  categoryAliases = [],
  subcategory,
  title,
}) => {
  const hasAliases = Array.isArray(categoryAliases) && categoryAliases.length > 0;
  
  const { news: adminNews = [] } = useNews(
    hasAliases 
      ? { language, state, category: categoryAliases.join(","), subcategory } 
      : { language, state, category, subcategory }
  );

  const normalizedAliases = hasAliases
    ? categoryAliases.map((c) => c.toLowerCase().trim())
    : [];

  const normalizedSubcategory = subcategory ? subcategory.toLowerCase().trim() : "";

  const matchesSubcategory = (item) =>
    !normalizedSubcategory ||
    (item.subcategory || "").toLowerCase().trim() === normalizedSubcategory;
    
  const filteredNews = hasAliases
    ? adminNews.filter((item) => {
        const itemCategory = (item.category || "").toLowerCase().trim();
        return normalizedAliases.includes(itemCategory);
      })
    : adminNews;

  // Subcategory pages: ONLY show subcategory-specific news.
  const displayData = normalizedSubcategory
    ? filteredNews.filter(matchesSubcategory)
    : filteredNews;

  const [visibleCount, setVisibleCount] = useState(PER_PAGE);

  const [blogNews, setBlogNews] = useState([]);

  const BASE_URL = getMediaBaseUrl();

  const mapToCard = (item) => {
    let imageUrl = "";
    if (item.thumbnail) {
      if (item.thumbnail.startsWith("http")) {
        imageUrl = item.thumbnail;
      } else if (item.thumbnail.startsWith("/images/")) {
        imageUrl = item.thumbnail;
      } else {
        imageUrl = `${BASE_URL}/uploads/news/${encodeURIComponent(item.thumbnail)}`;
      }
    }
    return {
      id: item.id || item._id,
      image: imageUrl,
      category: item.category || "",
      state: item.state,
      title: item.news_title || item.title || "",
      author: item.reporter_name || item.author || "न्यूज़ डेस्क",
      reporter_image: item.reporter_image
        ? item.reporter_image.startsWith("http")
          ? item.reporter_image
          : `${BASE_URL}/uploads/news/${encodeURIComponent(item.reporter_image)}`
        : null,
      date: item.createdAt ? new Date(item.createdAt).toLocaleDateString("hi-IN") : "",
      readTime: "2 मिनट पढ़ें",
      fullNews: item.description || item.fullNews || "",
      videoUrl: item.youtube_url || "",
    };
  };

  useEffect(() => {
    let isMounted = true;
    getNewsByFilter({ limit: 10 })
      .then((res) => {
        if (isMounted) setBlogNews((Array.isArray(res?.data) ? res.data : []).map(mapToCard));
      })
      .catch((err) => console.error("Error fetching latest news:", err));
    return () => {
      isMounted = false;
    };
  }, []);

  const handleLoadMore = () => {
    setVisibleCount((prev) => prev + PER_PAGE);
  };

  // Removed early return on error so fallbackData can be displayed

  // Removed unused staticData
  
  const pageTitle = title || category || state || "ताज़ा खबर";

  return (
    <div className="bg-gray-50 min-h-screen pb-12">
      {/* ================= PREMIUM CATEGORY BANNER ================= */}
      <div className="bg-gradient-to-br from-[#2d006b] to-[#4600a3] text-white py-6 md:py-8 mb-6 relative overflow-hidden shadow-inner">
        {/* Subtle Background Patterns */}
        <div className="absolute inset-0 opacity-10 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMiIgY3k9IjIiIHI9IjEiIGZpbGw9InJnYmEoMCwwLDAsMC4wNSkiLz48L3N2Zz4=')]" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-white opacity-10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 animate-pulse" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-brand-red opacity-10 rounded-full blur-2xl translate-y-1/3 -translate-x-1/4" />
        
        <div className="max-w-7xl mx-auto px-4 relative z-10 flex flex-col items-center text-center">
          <span className="text-purple-200 text-xs md:text-sm font-bold tracking-[0.2em] uppercase mb-1">कवरेज एक्सप्लोर करें</span>
          <h1 className="text-2xl md:text-4xl lg:text-5xl font-black capitalize tracking-tight mb-3 drop-shadow-md">
             {pageTitle}
          </h1>
          <div className="w-12 h-1 bg-gradient-to-r from-purple-200 to-white rounded-full opacity-90 shadow-[0_0_15px_rgba(255,255,255,0.5)]" />
        </div>
      </div>

      <TopAdBanner />

      <div className="max-w-[1400px] mx-auto px-4">
        <BreakingNews />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-4">
        {/* LEFT SIDEBAR (Ad + Weather) */}
        <div className="hidden lg:block lg:col-span-2">
          <div className="space-y-3 sticky top-[70px]">
            <LeftAdBanner />
            <div>
              <WeatherWidget defaultCity="खजुराहो" />
            </div>
          </div>
        </div>

        {/* MIDDLE CONTENT */}
        <div className="lg:col-span-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {displayData.length === 0 ? (
              <div className="col-span-full py-12 text-center text-gray-500 font-semibold bg-white rounded-xl shadow-sm border border-gray-100">
                इस श्रेणी में अभी कोई खबर नहीं है।
              </div>
            ) : (
              displayData.slice(0, visibleCount).map((item, idx) => {
                const BASE_URL = getMediaBaseUrl();
              
              // Handle image paths correctly for both API uploads and dummy data
              let imageUrl = logo;
              if (item.thumbnail) {
                if (item.thumbnail?.startsWith("http")) {
                  imageUrl = item.thumbnail;
                } else if (item.thumbnail?.startsWith("/images/")) {
                  imageUrl = item.thumbnail; // fallback if any local images remain
                } else {
                  imageUrl = `${BASE_URL}/uploads/news/${encodeURIComponent(item.thumbnail)}`;
                }
              }

              let reporterImageUrl = null;
              if (item.reporter_image) {
                 if (item.reporter_image?.startsWith("http")) {
                   reporterImageUrl = item.reporter_image;
                 } else {
                   reporterImageUrl = `${BASE_URL}/uploads/news/${encodeURIComponent(item.reporter_image)}`;
                 }
              }

              // Create an updated item with the proper image URLs
              const updatedItem = {
                ...item,
                thumbnail: imageUrl,
                reporter_image: reporterImageUrl
              };

              return (
                <NewsCard 
                  key={idx} 
                  image={updatedItem.thumbnail}
                  category={updatedItem.category}
                  state={updatedItem.state}
                  title={updatedItem.news_title || updatedItem.title}
                  author={updatedItem.reporter_name || updatedItem.author}
                  reporter_image={updatedItem.reporter_image}
                  date={updatedItem.createdAt ? new Date(updatedItem.createdAt).toLocaleDateString() : updatedItem.date}
                  readTime="2 Min"
                  fullNews={updatedItem.description || updatedItem.fullNews}
                  id={updatedItem.id || updatedItem._id}
                />
              );
            })
            )}
          </div>

          {/* LOAD MORE BUTTON */}
          {visibleCount < displayData.length && (
            <div className="mt-8 flex justify-center pb-8">
              <button
                onClick={handleLoadMore}
                className="bg-purple-700 hover:text-brand-red text-white font-bold py-3 px-8 rounded-full shadow-md transition-transform hover:scale-105 active:scale-95"
              >
                और लोड करें
              </button>
            </div>
          )}
          {/* 🔹 TOI-STYLE VIDEO HUB */}
          <section className="my-6 md:my-10 py-8 md:py-12 bg-rich-black -mx-4 px-4 lg:-mx-8 lg:px-8 rounded-none">
            <div className="flex justify-between items-end mb-6">
              <h2 className="text-2xl md:text-3xl font-black text-white uppercase tracking-wide flex items-center gap-3">
                <i className="fa-solid fa-circle-play text-brand-red"></i> वीडियो हब
              </h2>
              <Link to="/gallery/videos" className="text-gray-400 hover:text-white text-sm font-bold transition">सभी देखें &rarr;</Link>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Main Featured Video (Left, 8 cols) */}
              <div className="lg:col-span-8">
                <div className="bg-black rounded-lg overflow-hidden border border-zinc-800 relative">
                  <div className="absolute top-4 left-4 bg-brand-red text-white px-3 py-1 text-[11px] font-black rounded shadow-lg animate-pulse z-10 flex items-center gap-2 uppercase tracking-widest">
                    <div className="w-2 h-2 bg-white rounded-full"></div> लाइव
                  </div>
                  <div className="w-full aspect-video relative">
                    <iframe 
                      className="absolute inset-0 w-full h-full"
                      src="https://www.youtube.com/embed/L7fIF53G_P4?autoplay=0" 
                      title="YouTube Live Stream" 
                      frameBorder="0" 
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                      allowFullScreen
                    ></iframe>
                  </div>
                  <div className="p-4 bg-zinc-900 border-t border-zinc-800">
                    <h3 className="text-white text-lg md:text-xl font-bold">LIVE: देश-दुनिया की तमाम बड़ी खबरें, देखें सीधा प्रसारण</h3>
                    <p className="text-gray-400 text-sm mt-2">मंदाकिनी टाइम्स न्यूज़ डेस्क • 12K देख रहे हैं</p>
                  </div>
                </div>
              </div>

              {/* Playlist (Right, 4 cols) */}
              <div className="lg:col-span-4 bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden flex flex-col">
                <div className="bg-zinc-800 text-white font-bold px-4 py-3 border-b border-zinc-700 text-sm uppercase tracking-wide">
                  अप नेक्स्ट
                </div>
                <div className="flex-1 overflow-y-auto max-h-[350px] lg:max-h-[400px] p-2 space-y-2 scrollbar-thin scrollbar-thumb-zinc-700">
                  {videonews.map((item, i) => (
                    <div key={i} className="flex gap-3 p-2 hover:bg-zinc-800 rounded-md cursor-pointer transition group">
                      <div className="relative w-28 shrink-0 aspect-video rounded overflow-hidden">
                        <img src={item.image} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-300" />
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                          <i className="fa-solid fa-play text-white opacity-80 group-hover:opacity-100 group-hover:text-brand-red transition"></i>
                        </div>
                      </div>
                      <div className="flex flex-col justify-center">
                        <h4 className="text-gray-200 text-[13px] font-bold line-clamp-3 group-hover:text-brand-red transition leading-snug">
                          {item.title}
                        </h4>
                        <span className="text-zinc-500 text-[10px] mt-1 uppercase font-bold">{item.readTime || "2:45"}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* BLOGS SECTION */}
          <section className="my-8 pt-6 border-t border-gray-200">
            <ViewAllHeader title="ब्लॉग्स" to="/blogs" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(blogNews.length > 0 ? blogNews.slice(0, 3) : []).map((item, i) => (
                <NewsCard key={item.id || `blog-${i}`} {...item} hideListenButton={true} />
              ))}
            </div>
          </section>

        </div>

        {/* RIGHT */}
        <aside className="lg:col-span-2">
          <RightSidebar />
        </aside>
      </div>



    </div>
    </div>
  );
};

export default NewsPage;
